import * as Sentry from "@sentry/react";
import type { AnyRouter } from "@tanstack/react-router";
import { isRateLimitError } from "#/features/trade/lib/hyperliquid-error.ts";
import { isDomNodeError } from "#/lib/dom-node-error-handler.ts";
import { isPreloadError } from "#/lib/preload-error-handler.ts";

export function initSentry(router: AnyRouter) {
	if (!import.meta.env.PROD) return;
	if (import.meta.env.MODE !== "production") return;
	// Automated browsers — the deploy pipeline's prerender (Puppeteer) runs this
	// production bundle and was the sole source of every "iframe not initialized"
	// event (7/7 from HeadlessChrome@127.0.0.1, zero real users). Block by actor,
	// not error type, so real-user wallet errors stay visible.
	if (navigator.webdriver) return;
	const dsn = import.meta.env.VITE_SENTRY_DSN;
	if (!dsn) return;

	Sentry.init({
		dsn,
		environment: import.meta.env.MODE,
		release: import.meta.env.VITE_SENTRY_RELEASE,
		integrations: [
			Sentry.tanstackRouterBrowserTracingIntegration(router),
			// httpClientIntegration removed — Hyperliquid API errors are now
			// captured with richer context (query_domain, query_type, http_status,
			// api_endpoint tags + fingerprinting) by the TanStack Query/Mutation
			// onError handlers in root-provider.tsx.
			Sentry.extraErrorDataIntegration({ depth: 3 }),
		],
		tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,
		tracePropagationTargets: [
			/^https:\/\/api\.hyperliquid\.xyz/,
			// Main app domain only — excludes api.tenbagger.xyz, whose CORS does
			// not currently allow the `sentry-trace` request header. Including it
			// here would force a preflight that fails and turns every call into
			// `TypeError: Failed to fetch`.
			/^https:\/\/tenbagger\.xyz/,
		],
		sendDefaultPii: false,
		beforeSend(event, hint) {
			if (isRateLimitError(hint.originalException)) return null;
			// Defense in depth: drop httpClientIntegration's synthesized 429
			// events (no `originalException`, status carried in response context).
			if (event.contexts?.response?.status_code === 429) return null;
			// Vite code-split asset load failures (stale tab after a deploy →
			// old hashed chunk 404s). `initPreloadErrorHandler` already reloads
			// the page to recover, so these are expected, self-healing noise.
			if (isPreloadError(hint.originalException)) return null;
			if (isPreloadError(event.exception?.values?.[0]?.value)) return null;
			// Browser auto-translation / extensions mutate React-managed nodes,
			// producing `insertBefore`/`removeChild` "not a child" DOMExceptions.
			// `initDomNodeErrorGuard` (src/main.tsx) neutralizes them at the DOM
			// layer; this drops any residual that still surfaces here.
			if (isDomNodeError(hint.originalException)) return null;
			if (isDomNodeError(event.exception?.values?.[0]?.value)) return null;
			return event;
		},
	});
}

export function setSentryUser(address: string | null) {
	if (!import.meta.env.PROD) return;
	if (import.meta.env.MODE !== "production") return;
	if (!import.meta.env.VITE_SENTRY_DSN) return;
	if (address) {
		Sentry.setUser({ id: address });
	} else {
		Sentry.setUser(null);
	}
}
